import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  RefundVm,
  RefundLine,
  RefundListResponse,
  RefundListItem,
  RefundVendor,
  RefundBill,
  RefundBillLine,
  ApiResponse,
} from '@/api/types'
import { appendSortDir, type ListSortDir } from '@/lib/listSort'

/**
 * Vendor Refunds (Credit Notes) service
 */
export class RefundsService {
  /**
   * Get paginated list of refunds for a branch
   */
  async getRefunds(
    branchId: number,
    pageNumber: number,
    pageSize: number,
    sortDir: ListSortDir = 'desc'
  ): Promise<RefundListResponse> {
    try {
      const url = appendSortDir(endpoints.refunds.getAll(branchId, pageNumber, pageSize), sortDir)
      console.log('🔵 [RefundsService.getRefunds] Calling:', url, { branchId, pageNumber, pageSize })
      const response = await apiClient.get<ApiResponse<RefundListResponse> | RefundListResponse | any>(url)
      console.log('🔵 [RefundsService.getRefunds] Full Response:', response)
      console.log('🔵 [RefundsService.getRefunds] Response Data:', response.data)
      
      const payload = response.data

      if (payload && typeof payload === 'object') {
        if ('success' in payload && 'data' in payload) {
          console.log('🔵 [RefundsService.getRefunds] Returning from success.data')
          return payload.data as RefundListResponse
        }

        if ('data' in payload && Array.isArray(payload.data)) {
          console.log('🔵 [RefundsService.getRefunds] Returning from data property:', payload.data.length, 'items')
          console.log('🔵 [RefundsService.getRefunds] Sample item:', payload.data[0])
          return {
            totalRecords: payload.totalRecords ?? payload.data.length,
            pageNumber: payload.pageNumber ?? pageNumber,
            pageSize: payload.pageSize ?? pageSize,
            data: payload.data as RefundListItem[],
          }
        }
      }

      if (Array.isArray(payload)) {
        console.log('🔵 [RefundsService.getRefunds] Response is direct array:', payload.length, 'items')
        return {
          totalRecords: payload.length,
          pageNumber,
          pageSize,
          data: payload as RefundListItem[],
        }
      }

      console.warn('⚠️ [RefundsService.getRefunds] Unexpected response format:', payload)
      return {
        totalRecords: payload?.totalRecords ?? 0,
        pageNumber: payload?.pageNumber ?? pageNumber,
        pageSize: payload?.pageSize ?? pageSize,
        data: payload?.data ?? [],
      }
    } catch (error: any) {
      console.error('❌ [RefundsService.getRefunds] Error:', error)
      console.error('❌ [RefundsService.getRefunds] Error Response:', error.response)
      console.error('❌ [RefundsService.getRefunds] Error Data:', error.response?.data)
      throw error
    }
  }

  /**
   * Get all vendors for refunds
   */
  async getAllVendors(branchId: number): Promise<RefundVendor[]> {
    const url = endpoints.refunds.getAllVendors(branchId)
    const response = await apiClient.get<ApiResponse<RefundVendor[]> | RefundVendor[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get all bills for refunds (with remaining amount > 0)
   */
  async getAllBills(branchId: number): Promise<RefundBill[]> {
    const url = endpoints.refunds.getAllBills(branchId)
    const response = await apiClient.get<ApiResponse<RefundBill[]> | RefundBill[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get bill lines for a specific bill
   * API returns: { bill: {...}, billsLines: [...] }
   */
  async getBillLines(branchId: number, billId: number): Promise<{ bill: any; billsLines: RefundBillLine[] }> {
    try {
      const url = endpoints.refunds.getBillLines(branchId, billId)
      console.log('🔵 [RefundsService.getBillLines] Calling:', url, 'billId:', billId)
      const response = await apiClient.get<any>(url)
      console.log('🔵 [RefundsService.getBillLines] Full Response:', response)
      console.log('🔵 [RefundsService.getBillLines] Response Data:', response.data)
      
      const responseData = response.data
      
      // Handle response structure: { bill: {...}, billsLines: [...] }
      if (responseData && typeof responseData === 'object') {
        // Check if data is wrapped in 'data' property
        let data = responseData
        if ('data' in responseData && typeof responseData.data === 'object') {
          data = responseData.data
        }
        
        if ('billsLines' in data && Array.isArray(data.billsLines)) {
          console.log('🔵 [RefundsService.getBillLines] Found billsLines array:', data.billsLines.length, 'items')
          return {
            bill: data.bill || {},
            billsLines: data.billsLines,
          }
        }
        
        // Also check for 'billLines' (without 's')
        if ('billLines' in data && Array.isArray(data.billLines)) {
          console.log('🔵 [RefundsService.getBillLines] Found billLines array:', data.billLines.length, 'items')
          return {
            bill: data.bill || {},
            billsLines: data.billLines,
          }
        }
      }
      
      // Fallback: check if response.data is directly an array
      if (Array.isArray(responseData)) {
        console.log('🔵 [RefundsService.getBillLines] Response is direct array:', responseData.length, 'items')
        return {
          bill: {},
          billsLines: responseData,
        }
      }
      
      console.warn('⚠️ [RefundsService.getBillLines] Unexpected response format:', responseData)
      return { bill: {}, billsLines: [] }
    } catch (error: any) {
      console.error('❌ [RefundsService.getBillLines] Error:', error)
      console.error('❌ [RefundsService.getBillLines] Error Response:', error.response)
      console.error('❌ [RefundsService.getBillLines] Error Data:', error.response?.data)
      throw error
    }
  }

  /**
   * Get refund by ID with lines
   */
  async getRefundById(branchId: number, refundId: number): Promise<RefundVm> {
    const extractData = (data: any): any => {
      if (!data || typeof data !== 'object') return data
      if ('success' in data && 'data' in data) return extractData(data.data)
      if ('data' in data && data.data && typeof data.data === 'object' && !Array.isArray(data) && ('refundId' in data.data || 'RefundId' in data.data || Array.isArray(data.data))) {
        return extractData(data.data)
      }
      return data
    }

    const [headerResponse, linesResponse] = await Promise.all([
      apiClient.get<any>(endpoints.refunds.getById(refundId)),
      apiClient.get<any>(endpoints.refunds.getRefundLines(branchId, refundId)),
    ])

    const header = extractData(headerResponse.data) || {}
    const linesRaw = extractData(linesResponse.data)
    const lines = Array.isArray(linesRaw) ? linesRaw : []

    const mappedLines: RefundLine[] = lines.map((line: any) => {
      const qty = Number(line.qty ?? line.Qty ?? 0) || 0
      const unitPrice = Number(line.unitPrice ?? line.UnitPrice ?? 0) || 0
      const itemPrice = Number(line.itemPrice ?? line.ItemPrice ?? qty * unitPrice) || 0
      const discountAmount = Number(line.discountAmount ?? line.DiscountAmount ?? 0) || 0
      const taxAmount = Number(line.taxAmount ?? line.TaxAmount ?? 0) || 0
      const taxRate = Number(line.taxRate ?? line.TaxRate ?? 0) || 0
      return {
        lineId: Number(line.lineId ?? line.LineId ?? 0) || 0,
        billLineId: Number(line.billLineId ?? line.BillLineId ?? 0) || 0,
        refundId: Number(line.refundId ?? line.RefundId ?? refundId) || refundId,
        itemId: Number(line.itemId ?? line.ItemId ?? 0) || 0,
        description: String(line.description ?? line.Description ?? ''),
        qty,
        unitPrice,
        itemPrice,
        taxRateId: Number(line.taxRateId ?? line.TaxRateId ?? 0) || 0,
        taxRate,
        taxAmount,
        lineAmount: Number(line.lineAmount ?? line.LineAmount ?? itemPrice - discountAmount + taxAmount) || 0,
        creditAccount: Number(line.creditAccount ?? line.CreditAccount ?? 0) || 0,
        isDeleted: String(line.isDeleted ?? line.IsDeleted ?? 'false'),
        discountPercent: Number(line.discountPercent ?? line.DiscountPercent ?? 0) || 0,
        discountAmount,
      } as RefundLine
    })

    return {
      refundId: Number(header.refundId ?? header.RefundId ?? refundId) || refundId,
      refundDate: header.refundDate || header.RefundDate || '',
      billId: Number(header.billId ?? header.BillId ?? 0) || 0,
      dueDate: header.dueDate || header.DueDate || '',
      subTotal: Number(header.subTotal ?? header.SubTotal ?? 0) || 0,
      totalTax: Number(header.totalTax ?? header.TotalTax ?? 0) || 0,
      total: Number(header.total ?? header.Total ?? 0) || 0,
      debitAccount: Number(header.debitAccount ?? header.DebitAccount ?? 0) || 0,
      isFullyRefund: String(header.isFullyRefund ?? header.IsFullyRefund ?? ''),
      note: header.note || header.Note || '',
      branchId: Number(header.branchId ?? header.BranchId ?? branchId) || branchId,
      ipAddress: '',
      refundLine: mappedLines,
      isPosted: header.isPosted === true || header.isPosted === 1 || header.IsPosted === true,
      refundNo: header.refundNo || header.RefundNo || '',
      vendorId: Number(header.vendorId ?? header.VendorId ?? 0) || 0,
      vendorNameEn: header.vendorNameEn || header.VendorNameEn || '',
      billNo: header.billNo || header.BillNo || '',
      totalDiscount: Number(header.totalDiscount ?? header.TotalDiscount ?? 0) || 0,
      refNo: header.refNo || header.RefNo || '',
    }
  }

  /**
   * Save new refund
   */
  async saveRefund(refund: RefundVm): Promise<any> {
    const url = endpoints.refunds.insert
    const response = await apiClient.post<ApiResponse<any> | any>(url, refund)
    return response.data
  }

  /**
   * Update existing refund
   */
  async updateRefund(refund: RefundVm): Promise<any> {
    const url = endpoints.refunds.update
    const response = await apiClient.put<ApiResponse<any> | any>(url, refund)
    return response.data
  }

  async postRefund(id: number, ipAddress?: string): Promise<any> {
    const url = endpoints.refunds.postData(id, ipAddress)
    const response = await apiClient.post<any>(url)
    return response.data
  }
}

export const refundsService = new RefundsService()

